import React from 'react';
import * as ReactDOM from 'react-dom';
import { useDrawerEscape } from '../../../lib/hooks/useDrawerEscape';

interface FullPageSheetProps {
  open: boolean;
  onClose: () => void;
  /** Etichetta magenta in alto a sinistra: "Descrizione", "Rate e scadenze". */
  kind: string;
  /** Contesto: nome del servizio, numero offerta. */
  subtitle?: string;
  /** Nota grigia nel footer. */
  note?: string;
  children: React.ReactNode;
}

/**
 * Foglio centrale a tutta pagina, alla Google Docs: fondo scurito, pagina
 * bianca centrata, contenuto in colonna stretta. Serve quando la descrizione è
 * lunga o le rate sono molte e il pannello laterale sta stretto.
 *
 * Chiusura con Esc, con il bottone o cliccando fuori dal foglio. Il contenuto
 * vive nel chiamante: qui c'è solo la cornice.
 */
export function FullPageSheet({ open, onClose, kind, subtitle, note, children }: FullPageSheetProps) {
  // Escape condiviso: il foglio sta sopra il modale dell'offerta (z 20050)
  useDrawerEscape(open, onClose, 20050);

  if (!open || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div
      // Sopra il modale dell'offerta (z 20000), come il foglio della descrizione.
      className="fixed inset-0 z-[20050] bg-black/45 flex items-start justify-center px-4 py-[5vh] animate-[fadeIn_0.15s_ease-out]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* role="dialog" sul foglio e non sullo sfondo: una regola globale
          (html:not(.dark) [role="dialog"]) forza il bianco !important, e
          sull'overlay cancellava il fondo scuro. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={kind}
        className="w-full max-w-[820px] h-[85vh] max-h-[900px] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden"
      >
        <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-black/[0.07] dark:border-white/10 flex-shrink-0">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-[#E91E8C]">{kind}</span>
          {subtitle && (
            <span className="text-[12px] text-black/50 dark:text-white/50 truncate">{subtitle}</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-[12px] font-medium px-2.5 py-1 rounded-md border border-black/[0.1] dark:border-white/15 text-black/70 dark:text-white/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
          >
            Chiudi
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">{children}</div>

        {note && (
          <div className="flex-shrink-0 px-6 py-3 bg-black/[0.02] dark:bg-white/[0.03] border-t border-black/[0.07] dark:border-white/10">
            <p className="max-w-[640px] mx-auto text-[12px] text-black/40 dark:text-white/40">{note}</p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
